import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, CheckCircle2 } from 'lucide-react';

/**
 * PhoneInput
 * --------------------------------------------------------------------------
 * Telefon-Eingabe mit Ländervorwahl-Selector & per-Land Format-Validierung.
 *
 * Der Komponenten-`value` ist immer der ZUSAMMENGESETZTE String,
 * z. B. `"+49 1761234567"` oder leerer String.
 *
 * UX-Details
 *   • Default-Land: Deutschland (+49)
 *   • Führende `0` wird automatisch entfernt (deutsche Konvention)
 *   • Nur Ziffern + Leerzeichen werden akzeptiert
 *   • Per-Land Min/Max-Ziffern-Validierung
 *   • Grünes Häkchen rechts wenn gültig, rote Border bei zu wenig/zu vielen Ziffern
 *
 * Props
 *   value         : string  – aktuell zusammengesetzter Wert
 *   onChange(v)   : (v) => void
 *   placeholder   : string
 *   className     : string  – wird an den Eingabe-Wrapper durchgereicht
 *   inputClassName: string  – wird an das eigentliche <input> durchgereicht
 *   testId        : string  – data-testid Präfix
 *   required      : bool
 * --------------------------------------------------------------------------
 */
export const COUNTRY_DIAL_CODES = [
  // minLen/maxLen = erwartete Ziffern-Anzahl im LOKALEN Teil (ohne Vorwahl, ohne führende 0)
  { code: 'DE', flag: '🇩🇪', dial: '+49',  name: 'Deutschland',           minLen: 10, maxLen: 12 },
  { code: 'AT', flag: '🇦🇹', dial: '+43',  name: 'Österreich',            minLen: 9,  maxLen: 13 },
  { code: 'CH', flag: '🇨🇭', dial: '+41',  name: 'Schweiz',               minLen: 9,  maxLen: 9 },
  { code: 'FR', flag: '🇫🇷', dial: '+33',  name: 'Frankreich',            minLen: 9,  maxLen: 9 },
  { code: 'IT', flag: '🇮🇹', dial: '+39',  name: 'Italien',               minLen: 9,  maxLen: 11 },
  { code: 'NL', flag: '🇳🇱', dial: '+31',  name: 'Niederlande',           minLen: 9,  maxLen: 9 },
  { code: 'BE', flag: '🇧🇪', dial: '+32',  name: 'Belgien',               minLen: 8,  maxLen: 9 },
  { code: 'LU', flag: '🇱🇺', dial: '+352', name: 'Luxemburg',             minLen: 9,  maxLen: 9 },
  { code: 'PL', flag: '🇵🇱', dial: '+48',  name: 'Polen',                 minLen: 9,  maxLen: 9 },
  { code: 'GB', flag: '🇬🇧', dial: '+44',  name: 'Vereinigtes Königreich', minLen: 10, maxLen: 10 },
  { code: 'TR', flag: '🇹🇷', dial: '+90',  name: 'Türkei',                minLen: 10, maxLen: 10 },
  { code: 'US', flag: '🇺🇸', dial: '+1',   name: 'USA',                   minLen: 10, maxLen: 10 },
  { code: 'CA', flag: '🇨🇦', dial: '+1',   name: 'Kanada',                minLen: 10, maxLen: 10 },
  { code: 'AE', flag: '🇦🇪', dial: '+971', name: 'VAE',                   minLen: 8,  maxLen: 9 },
  { code: 'SA', flag: '🇸🇦', dial: '+966', name: 'Saudi-Arabien',         minLen: 8,  maxLen: 9 },
  { code: 'EG', flag: '🇪🇬', dial: '+20',  name: 'Ägypten',               minLen: 9,  maxLen: 10 },
  { code: 'MA', flag: '🇲🇦', dial: '+212', name: 'Marokko',               minLen: 9,  maxLen: 9 },
  { code: 'DZ', flag: '🇩🇿', dial: '+213', name: 'Algerien',              minLen: 8,  maxLen: 9 },
  { code: 'TN', flag: '🇹🇳', dial: '+216', name: 'Tunesien',              minLen: 8,  maxLen: 8 },
  { code: 'LB', flag: '🇱🇧', dial: '+961', name: 'Libanon',               minLen: 7,  maxLen: 8 },
  { code: 'JO', flag: '🇯🇴', dial: '+962', name: 'Jordanien',             minLen: 8,  maxLen: 9 },
  { code: 'SY', flag: '🇸🇾', dial: '+963', name: 'Syrien',                minLen: 8,  maxLen: 9 },
  { code: 'IQ', flag: '🇮🇶', dial: '+964', name: 'Irak',                  minLen: 10, maxLen: 10 },
  { code: 'IL', flag: '🇮🇱', dial: '+972', name: 'Israel',                minLen: 8,  maxLen: 9 },
  { code: 'PS', flag: '🇵🇸', dial: '+970', name: 'Palästina',             minLen: 8,  maxLen: 9 },
  { code: 'RU', flag: '🇷🇺', dial: '+7',   name: 'Russland',              minLen: 10, maxLen: 10 },
  { code: 'UA', flag: '🇺🇦', dial: '+380', name: 'Ukraine',               minLen: 9,  maxLen: 9 },
  { code: 'IR', flag: '🇮🇷', dial: '+98',  name: 'Iran',                  minLen: 10, maxLen: 10 },
  { code: 'IN', flag: '🇮🇳', dial: '+91',  name: 'Indien',                minLen: 10, maxLen: 10 },
  { code: 'CN', flag: '🇨🇳', dial: '+86',  name: 'China',                 minLen: 11, maxLen: 11 },
];

const DEFAULT_COUNTRY = COUNTRY_DIAL_CODES[0]; // Deutschland

/** Splittet einen kombinierten Wert in {dial, local} – best effort. */
const splitValue = (raw) => {
  if (!raw || typeof raw !== 'string') return { country: DEFAULT_COUNTRY, local: '' };
  const cleaned = raw.trim();
  if (!cleaned.startsWith('+')) {
    // Alte Daten ohne Landesvorwahl: 0xxx → DE-Default
    return { country: DEFAULT_COUNTRY, local: cleaned.replace(/^0+/, '') };
  }
  // Längste Match-Vorwahl finden (für +1 vs. +91 etc.)
  const sorted = [...COUNTRY_DIAL_CODES].sort((a, b) => b.dial.length - a.dial.length);
  const hit = sorted.find((c) => cleaned.startsWith(c.dial));
  if (hit) {
    return {
      country: hit,
      local: cleaned.slice(hit.dial.length).replace(/^[\s-]+/, '').replace(/^0+/, ''),
    };
  }
  return { country: DEFAULT_COUNTRY, local: cleaned.replace(/^\+/, '') };
};

const PhoneInput = ({
  value = '',
  onChange,
  placeholder = '176 1234567',
  className = '',
  inputClassName = '',
  testId = 'phone-input',
  required = false,
}) => {
  const initial = useMemo(() => splitValue(value), [value]);
  const [country, setCountry] = useState(initial.country);
  const [local, setLocal] = useState(initial.local);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  // Hinweis: Bewusster Verzicht auf bidirektionale Sync mit externem `value` –
  // das Formular setzt den Wert nach Submit auf '' (Reset) zurück. Da der lokale
  // State bei Mount aus `value` initialisiert wird, ist das ausreichend.

  // Schließen bei Klick außerhalb
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const emit = (nextCountry, nextLocal) => {
    const trimmed = (nextLocal || '').trim();
    onChange(trimmed ? `${nextCountry.dial} ${trimmed}` : '');
  };

  const handleLocalChange = (e) => {
    // Nur Ziffern + Leerzeichen + Bindestriche zulassen
    let v = (e.target.value || '').replace(/[^\d\s-]/g, '');
    // Führende Nullen entfernen (deutsche Konvention: 0176... → 176...)
    v = v.replace(/^0+/, '');
    setLocal(v);
    emit(country, v);
  };

  const handleCountrySelect = (c) => {
    setCountry(c);
    setOpen(false);
    emit(c, local);
  };

  // Validierung pro Land
  const digitsOnly = local.replace(/\D/g, '');
  const isEmpty = digitsOnly.length === 0;
  const isValid = !isEmpty && digitsOnly.length >= country.minLen && digitsOnly.length <= country.maxLen;
  const isInvalid = !isEmpty && !isValid;

  return (
    <div ref={dropdownRef} className={`relative flex items-stretch ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testId}-country-toggle`}
        title="Land wählen"
        className={`flex items-center gap-1 px-2.5 border border-r-0 bg-[#fbfcfd] text-sm text-[#1B3A5C] hover:bg-[#fbf7f0] rounded-l-sm ${
          isInvalid ? 'border-rose-400' : 'border-[#1B3A5C]/20'
        }`}
      >
        <span className="text-base leading-none">{country.flag}</span>
        <span className="font-mono text-[13px]">{country.dial}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#34536b]/60" />
      </button>
      <div className="relative flex-1 min-w-0">
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={local}
          onChange={handleLocalChange}
          placeholder={placeholder}
          required={required}
          data-testid={`${testId}-number`}
          aria-invalid={isInvalid}
          className={`w-full border px-3 py-2 pr-9 text-sm focus:outline-none rounded-r-sm ${inputClassName} ${
            isInvalid
              ? 'border-rose-400 focus:border-rose-500'
              : isValid
                ? 'border-emerald-400 focus:border-emerald-500'
                : 'border-[#1B3A5C]/20 focus:border-[#b8893d]'
          }`}
        />
        {isValid && (
          <CheckCircle2
            data-testid={`${testId}-valid`}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 pointer-events-none"
          />
        )}
        {isInvalid && (
          <span
            data-testid={`${testId}-hint`}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-rose-500 font-mono pointer-events-none whitespace-nowrap bg-white/90 px-1"
            title={`Erwartet: ${country.minLen}${country.minLen === country.maxLen ? '' : '–' + country.maxLen} Ziffern`}
          >
            {digitsOnly.length}/{country.minLen === country.maxLen ? country.minLen : `${country.minLen}–${country.maxLen}`}
          </span>
        )}
      </div>
      {open && (
        <div
          data-testid={`${testId}-country-list`}
          className="absolute z-50 top-full left-0 mt-1 w-72 max-h-72 overflow-y-auto bg-white border border-[#1B3A5C]/20 shadow-lg rounded-sm"
        >
          {COUNTRY_DIAL_CODES.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => handleCountrySelect(c)}
              data-testid={`${testId}-country-${c.code}`}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-[#fbf7f0] transition-colors ${
                c.code === country.code ? 'bg-[#fbf7f0]' : ''
              }`}
            >
              <span className="text-base">{c.flag}</span>
              <span className="font-mono text-[13px] text-[#1B3A5C] w-12">{c.dial}</span>
              <span className="text-[#34536b]">{c.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
